import {useState} from 'react'
import PropTypes from 'prop-types'
import {Flex, Text, Button} from '@chakra-ui/react'
import {useTranslation} from 'react-i18next'
import {Icon, CheckIcon} from '@chakra-ui/icons'
import useSWRMutation from 'swr/mutation'

import {Header} from '../InvitePayerToMarmaladeModal/components'
import {OpenInNewIcon} from '../../icons'
import {fetcher, defaultHeaders} from '../../../api/fetcher'
import {MarmaladeLogoInCircleIcon} from '../../icons/MarmaladeLogoInCircleIcon'
import {useRails} from '../../contexts/rails'

const TemplateModalStepContent = ({
  goToNextStep,
  articleUrl,
  providerIcon,
  currentStepObj,
  goToPreviousStep,
}) => {
  const {t} = useTranslation('invoiceEmailTemplates')
  const [openedInvoiceLink, setOpenedInvoiceLink] = useState(false)
  const [openedEmailLink, setOpenedEmailLink] = useState(false)

  const {id: step, currentStep, nextStep} = currentStepObj

  const {organisation, authenticityToken} = useRails()

  const onLinkClick = () => {
    switch (currentStep) {
      case 'invoice':
        setOpenedInvoiceLink(true)
        break
      case 'email':
        setOpenedEmailLink(true)
        break
      default:
        break
    }
  }

  const getCorrectLinkOpened = () => {
    switch (currentStep) {
      case 'invoice':
        return openedInvoiceLink
      case 'email':
        return openedEmailLink
      default:
        return false
    }
  }

  const linkOpened = getCorrectLinkOpened()

  const goToInvoiceTemplateStep = () => {
    setOpenedInvoiceLink(false)

    goToPreviousStep()
  }

  const {trigger: markTemplateSubmitted} = useSWRMutation(
    `/api/suppliers/${organisation.currentSupplier.id}/invoice_email_templates/mark_submitted/${currentStep}`,
    async (url) => {
      return fetcher(url, {
        method: 'PUT',
        headers: defaultHeaders({authenticityToken}),
      })
    },
  )

  const onMarkCompleteClick = () => {
    markTemplateSubmitted()

    goToNextStep(nextStep)
  }

  return (
    <>
      <Header
        goToPreviousStep={goToInvoiceTemplateStep}
        goToPreviousStepEnabled={currentStepObj.currentStep === 'email'}
      />
      <Flex justify="center">
        <Flex direction="column" width="400px" gap={10} pt={10}>
          <Text
            textTransform="uppercase"
            fontSize="sm"
            fontWeight="medium"
            lineHeight="110%"
          >
            {t('step', {step})}
          </Text>
          <Flex align="center">
            <MarmaladeLogoInCircleIcon boxSize="16" zIndex="1" />
            {providerIcon}
          </Flex>
          <Flex direction="column" gap={6}>
            <Text
              color="black"
              fontWeight="regular"
              fontSize="2xl"
              lineHeight="90%"
            >
              {t(`modal.${currentStep}.title`)}
            </Text>
            <Text
              color="black"
              fontWeight="regular"
              fontSize="regular"
              lineHeight="150%"
            >
              {t(`modal.${currentStep}.description`)}
            </Text>
            {!linkOpened && (
              <Button
                className="fs-unmask"
                variant="primary"
                as="a"
                href={articleUrl}
                onClick={onLinkClick}
                width="30%"
                target="_blank"
                _hover={{}}
              >
                <Flex gap={1} alignItems="center">
                  <Text textTransform="uppercase">
                    {t('modal.buttons.hrefLink')}
                  </Text>
                  <Icon as={OpenInNewIcon} boxSize={4} />
                </Flex>
              </Button>
            )}
            {linkOpened && (
              <Button
                className="fs-unmask"
                borderRadius="full"
                backgroundColor="mld.primary.200"
                width="38%"
                onClick={onMarkCompleteClick}
                _hover={{}}
              >
                <Flex gap={1} alignItems="center">
                  <Text textTransform="uppercase">
                    {t('modal.buttons.markComplete')}
                  </Text>
                  <Icon as={CheckIcon} boxSize={4} />
                </Flex>
              </Button>
            )}
          </Flex>
        </Flex>
      </Flex>
    </>
  )
}

TemplateModalStepContent.propTypes = {
  goToNextStep: PropTypes.func.isRequired,
  articleUrl: PropTypes.string.isRequired,
  providerIcon: PropTypes.element.isRequired,
  currentStepObj: PropTypes.shape({
    id: PropTypes.number.isRequired,
    previousStep: PropTypes.string,
    currentStep: PropTypes.string.isRequired,
    nextStep: PropTypes.string,
  }).isRequired,
  goToPreviousStep: PropTypes.func.isRequired,
}

export default TemplateModalStepContent
